import React, { useState, useEffect } from 'react';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Plus, X, ExternalLink } from 'lucide-react';
import { supabase } from '../../../lib/supabaseClient';
import SkillsInput from '../../ui/skills-input';

const PLATFORM_TYPES = [
  { value: 'LinkedIn', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/username' },
  { value: 'GitHub', label: 'GitHub', placeholder: 'https://github.com/username' },
  { value: 'Portfolio', label: 'Portfolio', placeholder: 'https://yourportfolio.com' },
  { value: 'Other', label: 'Other', placeholder: 'https://...' },
];

const StepPreferences = ({ formData, setFormData }) => {
  const [availableSkills, setAvailableSkills] = useState([]);
  const [newLink, setNewLink] = useState({ type: 'LinkedIn', customType: '', url: '' });
  const [linkError, setLinkError] = useState(null);

  useEffect(() => {
    fetchSkills();
  }, []);

  const fetchSkills = async () => {
    const { data } = await supabase
      .from('skills_master')
      .select('id, name, category')
      .eq('status', 'active')
      .order('name');
    if (data) setAvailableSkills(data);
  };

  const update = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAvailabilityChange = (newStatus) => {
    // Clear availability_open_date if not "open_soon"
    if (newStatus !== 'open_soon') {
      setFormData(prev => ({
        ...prev,
        availability_status: newStatus,
        availability_open_date: '',
      }));
    } else {
      update('availability_status', newStatus);
    }
  };

  const handleAddLink = () => {
    setLinkError(null);

    if (!newLink.url.trim()) {
      setLinkError('URL is required');
      return;
    }

    if (newLink.type === 'Other' && !newLink.customType.trim()) {
      setLinkError('Platform name is required for Other type');
      return;
    }

    // Basic URL validation
    try {
      new URL(newLink.url);
    } catch (e) {
      setLinkError('Please enter a valid URL');
      return;
    }

    const linkToAdd = {
      type: newLink.type,
      ...(newLink.type === 'Other' && { customType: newLink.customType }),
      url: newLink.url.trim(),
      status: 'pending',
      added_at: new Date().toISOString(),
    };

    const currentLinks = formData.social_links || [];
    update('social_links', [...currentLinks, linkToAdd]);
    setNewLink({ type: 'LinkedIn', customType: '', url: '' });
  };

  const handleRemoveLink = (index) => {
    const currentLinks = formData.social_links || [];
    update('social_links', currentLinks.filter((_, i) => i !== index));
  };

  const getDisplayName = (link) => {
    return link.type === 'Other' && link.customType ? link.customType : link.type;
  };

  const socialLinks = formData.social_links || [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Preferences</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Set your availability and contact preferences. Everything here is optional.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="availability_status">Availability</Label>
        <select
          id="availability_status"
          value={formData.availability_status || 'open'}
          onChange={(e) => handleAvailabilityChange(e.target.value)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="open">Open to opportunities</option>
          <option value="open_soon">Available soon</option>
          <option value="closed">Not looking</option>
        </select>
      </div>

      {/* Date Available - shown when "open_soon" is selected */}
      {formData.availability_status === 'open_soon' && (
        <div className="space-y-2">
          <Label htmlFor="availability_open_date">Date Available</Label>
          <Input
            id="availability_open_date"
            type="date"
            value={formData.availability_open_date || ''}
            onChange={(e) => update('availability_open_date', e.target.value)}
            min={new Date().toISOString().split('T')[0]}
          />
        </div>
      )}

      <div className="space-y-3">
        <Label>Contact Preferences</Label>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.open_to_new_roles !== false}
              onChange={(e) => update('open_to_new_roles', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm">Open to new roles</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.open_to_client_contact !== false}
              onChange={(e) => update('open_to_client_contact', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-sm">Open to client contact requests</span>
          </label>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="preferred_contact_channel">Preferred Contact Method</Label>
        <select
          id="preferred_contact_channel"
          value={formData.preferred_contact_channel || 'email'}
          onChange={(e) => update('preferred_contact_channel', e.target.value)}
          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <option value="email">Email</option>
          <option value="linkedin">LinkedIn</option>
          <option value="both">Both</option>
        </select>
      </div>

      {/* Social & Professional Links */}
      <div className="space-y-3">
        <Label>Social & Professional Links</Label>
        <p className="text-xs text-muted-foreground">
          Add your professional profiles (pending verification before public display)
        </p>

        {/* Existing Links */}
        {socialLinks.length > 0 && (
          <div className="space-y-2">
            {socialLinks.map((link, index) => (
              <div
                key={index}
                className="flex items-center justify-between gap-2 p-2 border rounded-md bg-muted/30"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{getDisplayName(link)}</span>
                    <Badge variant="outline" className="text-xs">Pending</Badge>
                  </div>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline flex items-center gap-1 truncate"
                  >
                    {link.url}
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  </a>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveLink(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Add New Link Form */}
        <div className="border rounded-md p-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="platform-type" className="text-xs">Platform</Label>
              <select
                id="platform-type"
                value={newLink.type}
                onChange={(e) => setNewLink({ ...newLink, type: e.target.value, customType: '' })}
                className="w-full mt-1 h-9 px-3 py-1 text-sm border rounded-md bg-background"
              >
                {PLATFORM_TYPES.map((platform) => (
                  <option key={platform.value} value={platform.value}>
                    {platform.label}
                  </option>
                ))}
              </select>
            </div>
            {newLink.type === 'Other' && (
              <div>
                <Label htmlFor="custom-type" className="text-xs">Platform Name</Label>
                <Input
                  id="custom-type"
                  value={newLink.customType}
                  onChange={(e) => setNewLink({ ...newLink, customType: e.target.value })}
                  placeholder="e.g., Behance"
                  className="mt-1 h-9"
                />
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="link-url" className="text-xs">URL</Label>
            <Input
              id="link-url"
              type="url"
              value={newLink.url}
              onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
              placeholder={PLATFORM_TYPES.find((p) => p.value === newLink.type)?.placeholder || 'https://...'}
              className="mt-1 h-9"
            />
          </div>
          {linkError && (
            <p className="text-xs text-destructive">{linkError}</p>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddLink}
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add Link
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Skills</Label>
        <SkillsInput
          skills={formData.skills || []}
          onChange={(skills) => update('skills', skills)}
          availableSkills={availableSkills}
          allowFreeText={true}
          placeholder="Search and add your skills..."
        />
      </div>
    </div>
  );
};

export default StepPreferences;
